import React, { useRef } from 'react';
import { Signal } from '../../../../types/signals-live';
import IconWrapper from '../../../IconWrapper';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import SeverityCircle from '../../shared/SeverityCircle';
import ProcessingStatusPill, { ProgressInfo } from './ProcessingStatusPill';
import TaxonomyVersionBadge from './TaxonomyVersionBadge';
import SignalDetailPanel from './SignalDetailPanel';

export type RowHandlers = {
  onToggleExpand: (id: string) => void;
  onToggleThread: (id: string) => void;
  onToggleSelect: (id: string, checked: boolean) => void;
};

export type Columns = { select: string; severity: string; date: string; customer: string; summary: string; taxonomyVersion: string; complaint: string; vulnerability: string; chevron: string };

type Props = {
  signals: Signal[];
  detailCache: Record<string, any>;
  expanded: Record<string, boolean>;
  threadOpen: Record<string, boolean>;
  selectedIds: Set<string>;
  col: Columns;
  visibleColumns?: Set<string>;
  formatDate: (d?: string) => string;
  handleReclassifySignal: (id: string) => Promise<void>;
  handleLabelTap: (meta: any) => void;
  openFeedback: (id: string) => void;
  listScrollRef: React.RefObject<HTMLDivElement | null>;
  progressTick: number;
  justUpdated?: Record<string, boolean>;
} & RowHandlers;

const SignalsList: React.FC<Props> = ({
  signals,
  detailCache,
  expanded,
  threadOpen,
  selectedIds,
  col,
  visibleColumns,
  formatDate,
  handleReclassifySignal,
  handleLabelTap,
  openFeedback,
  listScrollRef,
  progressTick,
  justUpdated,
  onToggleExpand,
  onToggleThread,
  onToggleSelect,
}) => {
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);

  return (
    <div className="flex-1 overflow-y-auto pb-6" ref={listScrollRef}>
      {signals.map((s, idx) => {
        const isExpanded = !!expanded[s.id];
        const detail = (detailCache[s.id] as any) || (s as any);
        const prog = detail?._progress as ProgressInfo;
        const status = detail?.processing_status || s.processing_status;

        const isJustUpdated = justUpdated?.[s.id] || false;

        return (
          <div key={s.id} className={`${idx % 2 === 1 ? 'bg-[rgba(244,244,244,0.5)]' : ''} border-b border-[#F4F4F4] ${isJustUpdated ? 'animate-pulse-success' : ''}`}>
            <div
              className={`group flex items-center h-[64px] px-6 hover:bg-gray-50 hover:shadow-sm hover:ring-1 ring-gray-200 transition-shadow cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4342FF]/30 ${isJustUpdated ? 'bg-green-50' : ''}`}
              role="button"
              tabIndex={0}
              aria-expanded={isExpanded}
              aria-controls={`signal-panel-${s.id}`}
              ref={(el) => {
                rowRefs.current[idx] = el;
              }}
              onClick={() => onToggleExpand(s.id)}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggleExpand(s.id);
                  return;
                }
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  const next = Math.min(idx + 1, signals.length - 1);
                  const el = rowRefs.current[next];
                  if (el) {
                    el.focus();
                    el.scrollIntoView({ block: 'nearest' });
                  }
                  return;
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  const prev = Math.max(idx - 1, 0);
                  const el = rowRefs.current[prev];
                  if (el) {
                    el.focus();
                    el.scrollIntoView({ block: 'nearest' });
                  }
                  return;
                }
                if (e.key === 'Home') {
                  e.preventDefault();
                  const el = rowRefs.current[0];
                  if (el) {
                    el.focus();
                    el.scrollIntoView({ block: 'nearest' });
                  }
                  return;
                }
                if (e.key === 'End') {
                  e.preventDefault();
                  const last = Math.max(signals.length - 1, 0);
                  const el = rowRefs.current[last];
                  if (el) {
                    el.focus();
                    el.scrollIntoView({ block: 'nearest' });
                  }
                  return;
                }
                if (e.key === 'PageDown') {
                  e.preventDefault();
                  const container = listScrollRef.current;
                  const rowH = rowRefs.current[idx]?.clientHeight || 64;
                  const perPage = container ? Math.max(1, Math.floor(container.clientHeight / rowH) - 1) : 8;
                  const next = Math.min(idx + perPage, signals.length - 1);
                  const el = rowRefs.current[next];
                  if (el) {
                    el.focus();
                    el.scrollIntoView({ block: 'nearest' });
                  }
                  return;
                }
                if (e.key === 'PageUp') {
                  e.preventDefault();
                  const container = listScrollRef.current;
                  const rowH = rowRefs.current[idx]?.clientHeight || 64;
                  const perPage = container ? Math.max(1, Math.floor(container.clientHeight / rowH) - 1) : 8;
                  const prev = Math.max(idx - perPage, 0);
                  const el = rowRefs.current[prev];
                  if (el) {
                    el.focus();
                    el.scrollIntoView({ block: 'nearest' });
                  }
                  return;
                }
              }}
            >
              {/* Select */}
              <div className={col.select} onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(s.id)}
                  onChange={(e) => onToggleSelect(s.id, e.target.checked)}
                  className="rounded border-gray-300 text-[#4342FF] focus:ring-[#4342FF]"
                  aria-label={`Select signal ${s.id}`}
                />
              </div>
              {/* Severity */}
              <div className={`${col.severity} flex justify-start`}>
                <SeverityCircle score={s.severity_score} size="small" />
              </div>
              {/* Date */}
              <div className={`${col.date} text-[12px] font-semibold text-[rgba(38,38,40,0.8)]`}>{formatDate(s.received_at || s.created_at)}</div>
              {/* Customer */}
              <div className={`${col.customer} text-[12px] font-semibold text-figma-text-primary`}>
                {s.customer_name || s.customer?.name || '—'}
              </div>
              {/* Summary */}
              <div className={col.summary}>
                <div className="text-[13px] font-bold text-[#262628] mb-1 line-clamp-2">
                  {s.summary || 'Untitled Signal'}
                </div>
                <div className="flex items-center gap-2">
                  <ProcessingStatusPill status={status} progress={prog} tick={progressTick} />
                </div>
              </div>
              {/* Taxonomy Version */}
              {(!visibleColumns || visibleColumns.has('taxonomyVersion')) && (
                <div className={col.taxonomyVersion}>
                  <TaxonomyVersionBadge
                    version={detail?.taxonomy_version || s.taxonomy_version || detail?.classification?.taxonomy_version || s.classification?.taxonomy_version}
                    classifiedAt={detail?.classification?.created_at || s.classification?.created_at}
                  />
                </div>
              )}
              {/* Complaint */}
              <div className={col.complaint}>
                {s.complaint_detected ? (
                  <span className="text-[11px] font-semibold text-[#ff7950]">Detected</span>
                ) : (
                  <span className="text-[11px] font-semibold text-[#348b91]">None</span>
                )}
              </div>
              {/* Vulnerability */}
              <div className={col.vulnerability}>
                {s.vulnerability_detected ? (
                  <span className="text-[11px] font-semibold text-[#ff7950]">Detected</span>
                ) : (
                  <span className="text-[11px] font-semibold text-[#348b91]">None</span>
                )}
              </div>
              {/* Expand */}
              <button className={`${col.chevron} text-gray-500`} onClick={(e) => { e.stopPropagation(); onToggleExpand(s.id); }} aria-label={isExpanded ? 'Collapse' : 'Expand'}>
                <IconWrapper icon={isExpanded ? FiChevronDown : FiChevronRight} className="w-4 h-4" />
              </button>
            </div>

            {/* Expanded panel */}
            {isExpanded && (
              <SignalDetailPanel
                signal={s}
                detail={detail}
                progressTick={progressTick}
                onReclassify={handleReclassifySignal}
                onLabelTap={handleLabelTap}
                onOpenFeedback={openFeedback}
                formatDate={formatDate}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default React.memo(SignalsList);
